import React, { useState, useEffect, useCallback } from "react";
import { BsArrowDown } from "react-icons/bs";
import { OverlayTrigger, Tooltip, Col, Row, Spinner } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import { BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import EfisModal from "../../../components/UI/EfisModal/EfisModal";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import { formatarData } from "../../../components/Formatters/DateTime";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import useBuscaAnexo from "./hooks/useBuscaAnexo";
import constants from "../../Constants";

const ConsultaAnexos = ({ sqAcaoFiscal, sqOrdemServico, sqComunicado, sqAtividade, permissao, anexosOs }) => {
  const [showAlerta, setShowAlerta] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [showExcluirConfirm, setShowExcluirConfirm] = useState({
    show: false,
    body: null,
    context: null,
  });

  const {
    request: listaAnexosRequest,
    data: listaAnexosData,
    errors: listaAnexoserror,
    isLoading: listaAnexosLoading,
  } = useApiClient();

  const {
    request: deleteItemRequest,
    isLoading: deleteItemIsLoading,
    data: deleteItemData,
    errors: deleteItemErrors,
  } = useApiClient();

  const [sqItemBaixando, setSqItemBaixando] = useState(null);

  const { baixarAnexo, isLoading: downloadLoading } = useBuscaAnexo();

  const handleDownload = (row) => {
    setSqItemBaixando(row);
  };

  useEffect(() => {
    if (!sqItemBaixando) return;

    const executarDownload = async () => {
      try {
        await baixarAnexo(sqAcaoFiscal, sqItemBaixando.sequencial, sqItemBaixando.nomeArquivo);
      } finally {
        setSqItemBaixando(null);
      }
    };

    executarDownload();
  }, [sqItemBaixando, baixarAnexo, sqAcaoFiscal]);

  const acoes = (newRow) => {
    const loadingThisRow = downloadLoading && sqItemBaixando?.sequencial === newRow.sequencial;

    return (
      <>
        <OverlayTrigger placement="top" overlay={<Tooltip>Baixar arquivo</Tooltip>}>
          <button
            type="button"
            className="btn btn-link pa-0 p-0 mx-1 d-flex align-items-center"
            disabled={loadingThisRow}
            onClick={(e) => {
              e.preventDefault();
              handleDownload(newRow);
            }}
          >
            <BsDownload size={16} className="ml-1" />
          </button>
        </OverlayTrigger>

        {permissao ? (
          <OverlayTrigger placement="top" overlay={<Tooltip>{`Excluir ${newRow.nomeArquivo}?`}</Tooltip>}>
            <MdDelete
              onClick={() =>
                setShowExcluirConfirm({
                  show: true,
                  body: `Deseja realmente excluir arquivo: ${newRow.nomeArquivo}?`,
                  context: newRow.sequencial,
                })
              }
              className="text-danger p-0 btn-lg btn-bg-transparent"
              title="Excluir"
            />
          </OverlayTrigger>
        ) : null}
      </>
    );
  };

  const columns = [
    {
      name: "Descrição",
      cell: (row) => (row.descricao ? `${row.nomeArquivo} -> ${row.descricao}` : row.nomeArquivo),
    },
    {
      name: "Atividade Vinculada",
      cell: (row) => row.atividadeVinculada,
    },
    {
      name: "Inclusão",
      cell: (row) =>
        row.documentoAlteracao
          ? `${row.documentoAlteracao} - ${row.nmFormalAlteracao} - ${formatarData(
              row.tmAlteracao,
              "dd/MM/yyyy HH:mm:ss",
            )}`
          : `${row.documentoInclusao} - ${row.nmFormalInclusao} - ${formatarData(
              row.tmInclusao,
              "dd/MM/yyyy HH:mm:ss",
            )}`,
    },
    {
      name: "Ações",
      center: true,
      maxWidth: "1%",
      cell: (row) => acoes(row),
    },
  ];

  useEffect(() => {
    if (sqOrdemServico && ((sqComunicado && sqAtividade) || anexosOs)) {
      const params = {
        sequencialOrdemServico: sqOrdemServico,
        sequencialComunicado: sqComunicado,
        sequencialAtividade: sqAtividade,
        pageSize,
        pageNumber,
      };
      listaAnexosRequest(apiRoutes.fiscalizacao.ordemservico.consultarAnexosAtividade, params);
    }
  }, [listaAnexosRequest, pageNumber, pageSize, sqAtividade, sqComunicado, sqOrdemServico, anexosOs]);

  useEffect(() => {
    if (listaAnexosData && listaAnexosData.result) {
      setAnexos(listaAnexosData.result);
      setTotalRows(listaAnexosData.result.length);
    }
    if (listaAnexoserror) {
      toast.error(listaAnexoserror.message);
    }
  }, [listaAnexosData, listaAnexoserror]);

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  const refreshItems = useCallback(() => {
    handlePageChange();
  }, [handlePageChange]);

  useEffect(() => {
    if (!deleteItemData && !deleteItemErrors) return;

    if (deleteItemData) {
      toast.success("Item excluído com sucesso.");
      refreshItems();
    }

    if (deleteItemErrors) {
      setShowAlerta(true);
    }

    setShowExcluirConfirm({ show: false });
  }, [deleteItemData, deleteItemErrors, refreshItems, setShowExcluirConfirm]);

  return (
    <>
      <EfisModal
        show={showExcluirConfirm.show}
        body={showExcluirConfirm.body}
        loadingConfirmationAction={deleteItemIsLoading}
        closeHandler={() => setShowExcluirConfirm({ show: false })}
        confirmationHandler={() =>
          deleteItemRequest(apiRoutes.fiscalizacao.ordemservico.excluirAnexoAtividade, {
            sequenceAcaoFiscal: sqAcaoFiscal,
            sequenceItem: showExcluirConfirm.context,
          })
        }
      />
      <FormAlert
        show={showAlerta}
        errors={deleteItemErrors}
        isLoading={deleteItemIsLoading}
        closeHandler={() => setShowAlerta(false)}
      />
      {downloadLoading && (
        <div className="d-flex align-items-center mb-2">
          <Spinner animation="border" size="sm" role="status" className="mr-2" />
          <span style={{ color: "#e37023", fontWeight: 500 }}>Baixando arquivo...</span>
        </div>
      )}
      {(anexos || []) && (
        <>
          <div style={{ width: "100%" }}>
            <Row>
              <Col>
                <DataTable
                  noHeader
                  keyField="sequencial"
                  columns={columns}
                  data={anexos}
                  progressPending={listaAnexosLoading}
                  noDataComponent="Não há anexos para esta OS"
                  progressComponent={<TextSpinner />}
                  defaultSortFieldId={1}
                  sortIcon={<BsArrowDown />}
                  expandableRows={!!anexos.length}
                  pagination
                  sortable
                  paginationTotalRows={totalRows}
                  pointerOnHover
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationComponentOptions={constants.paginationComponentOptions}
                />
              </Col>
            </Row>
          </div>
        </>
      )}
    </>
  );
};

export default ConsultaAnexos;
